import { Component, ElementRef, ViewChild, OnInit, Input, output, OnDestroy, AfterViewInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { QRCodeComponent } from 'angularx-qrcode';

// Input interface with optional properties
export interface QrCodeConfig {
  // Required properties
  qrData: string;
  ariaLabel: string;
  alt: string;

  // Optional properties with defaults
  allowEmptyString?: boolean;
  imageSrc?: string;
  imageHeight?: number;
  imageWidth?: number;
  cssClass?: string;
  colorDark?: string;
  colorLight?: string;
  elementType?: 'canvas' | 'svg' | 'img';
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  margin?: number;
  scale?: number;
  width?: number;
  title?: string;
}

// Internal interface with all properties required
type QrCodeConfigRequired = Required<Omit<QrCodeConfig, 'imageSrc' | 'imageHeight' | 'imageWidth'>> & {
  imageSrc?: string;
  imageHeight?: number;
  imageWidth?: number;
};

@Component({
  selector: 'app-qr-code-handler-component',
  imports: [QRCodeComponent, TranslateModule],
  templateUrl: './qr-code-handler-component.component.html',
})
export class QrCodeHandlerComponentComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('qrcodeElement', { read: ElementRef }) qrcodeElement!: ElementRef;
  @Input() qrOptions!: QrCodeConfig;
  @Input() errorLabel = 'PAYMENT_REPORT.sorryQrCodeGenerationIsUnavailable';
  @Input() id = '';
  clickOutput = output();
  qrCodeErrorOutput = output<boolean>();

  protected qrCodeError = false;
  private observer!: MutationObserver;
  public qrData = '';

  private readonly defaultOptions: QrCodeConfigRequired = {
    qrData: '',
    ariaLabel: 'QR Code',
    alt: 'QR Code',
    cssClass: '',
    colorDark: '#000000ff',
    colorLight: '#ffffffff',
    elementType: 'svg',
    errorCorrectionLevel: 'H',
    margin: 4,
    scale: 20,
    width: 225,
    title: 'Qr-code',
    allowEmptyString: false,
  };

  protected get options(): QrCodeConfigRequired {
    return {
      ...this.defaultOptions,
      ...this.qrOptions,
    };
  }

  ngOnInit(): void {
    this.qrData = this.qrOptions.qrData;
  }

  private initQrCodeObserver(): void {
    if (!this.qrcodeElement?.nativeElement) {
      this.setQrCodeError(true);
      return;
    }

    const targetNode = this.qrcodeElement.nativeElement;
    this.checkQrCodeElement(targetNode);

    this.observer = new MutationObserver(() => {
      this.checkQrCodeElement(targetNode);
    });

    this.observer.observe(targetNode, { childList: true, subtree: true });
  }

  private checkQrCodeElement(element: HTMLElement): void {
    const hasCanvasOrSvg = element.querySelector('canvas, svg');
    this.setQrCodeError(!hasCanvasOrSvg);
  }

  reloadQRCode() {
    const qrData = this.qrOptions.qrData;
    this.qrOptions.qrData = qrData;
    this.setQrCodeError(false);
  }

  private setQrCodeError(hasError: boolean): void {
    this.qrCodeError = hasError;
    this.qrCodeErrorOutput.emit(this.qrCodeError);
  }

  ngAfterViewInit(): void {
    this.initQrCodeObserver();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
